/**
 * SellerCurrencyPicker — compact dropdown letting the seller choose which
 * currency their dashboard money is shown in. Defaults to "Auto" (their
 * billing country); an explicit pick is remembered via localStorage.
 */
import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Coins } from "lucide-react";
import { SUPPORTED_CURRENCIES } from "../../lib/money";
import { useSellerCurrency } from "../../lib/sellerCurrency";

export default function SellerCurrencyPicker() {
  const { currency, billingCurrency, isAuto, setCurrency } = useSellerCurrency();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="seller-currency-btn"
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#261534] border border-[#3D2845] hover:border-[#D4FF00] transition text-xs font-bold uppercase tracking-wider"
        title="Show dashboard money in"
      >
        <Coins size={14} className="text-[#D4FF00]" />
        {currency}
        <ChevronDown size={13} className="text-[#A1A1AA]" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-56 bg-[#261534] border border-[#3D2845] rounded-2xl z-50 shadow-2xl overflow-hidden"
          data-testid="seller-currency-menu"
        >
          <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA] border-b border-[#3D2845]">
            Show my money in
          </div>
          <div className="max-h-72 overflow-y-auto">
            <button
              type="button"
              onClick={() => { setCurrency(""); setOpen(false); }}
              data-testid="seller-currency-opt-auto"
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#3D2845] transition ${isAuto ? "bg-[#D4FF00]/10 text-[#D4FF00]" : ""}`}
            >
              <span className="flex-1 text-left">Auto · billing ({billingCurrency})</span>
              {isAuto && <Check size={14} />}
            </button>
            {SUPPORTED_CURRENCIES.map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => { setCurrency(code); setOpen(false); }}
                data-testid={`seller-currency-opt-${code}`}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#3D2845] transition ${!isAuto && currency === code ? "bg-[#D4FF00]/10 text-[#D4FF00]" : ""}`}
              >
                <span className="flex-1 text-left">{code}</span>
                {!isAuto && currency === code && <Check size={14} />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
